import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import AvailabilityAction from '~/constants/availabilityAction';
import AvailabilityError from '~/constants/availabilityError';
import RcbOrderState from '~/constants/home/orderState';
import { InventoryTypeKey } from '~/constants/inventoryType';
import OrderState from '~/constants/orderState';
import { VehicleLockTypeKey } from '~/constants/vehicleLockType';
import type {
  Availability,
  Purchasing,
  VehiclePaymentTerm
} from '~/contracts/vdp';
import RcbPurchasing from '~/contracts/vdp/RcbPurchasing';
import { VdpPricingResponse } from '~/hooks/vdp/services/useGetVDPPricing';
import { PricingError } from '~/services/srp/contracts/PricingResponse';
import type { DeepPartial } from '~/types';
import EventManager, { useEventTrigger } from '~/utilities/EventManager';

type TransitRoute = {
  activityType: string | null;
  charge: number;
  routeId: string | null;
};

export type ContextType = {
  fetchVdpPricingFlag: number;
  fetchVehicleAvailabilityFlag: number;
  isFavorite: boolean;
  isSubscribedToNotifications: boolean;
  purchasing: LoadableState<Purchasing>;
  rcbPurchasing: LoadableState<RcbPurchasing>;
  setFavorite: (isFavorite: boolean, isSubscribed?: boolean) => void;
  setSubscribedToNotifications: React.Dispatch<React.SetStateAction<boolean>>;
  setPricingError: React.Dispatch<React.SetStateAction<PricingError>>;
  subscribeToNotificationsState: LoadableState<boolean>;
  transitRoute: LoadableState<TransitRoute>;
  triggerEvent: EventManager;
  triggerFetchVdpPricing: () => void;
  triggerFetchVehicleAvailability: () => void;
  vdpPricingInfo: LoadableState<Partial<VdpPricingResponse>>;
  vdpPricingError: PricingError;
  vehicleAvailability: LoadableState<Availability>;
};

const defaultContext: ContextType = {
  fetchVdpPricingFlag: 0,
  fetchVehicleAvailabilityFlag: 0,
  isFavorite: false,
  isSubscribedToNotifications: false,
  purchasing: defaultLoadableState<Purchasing>({
    hasActiveCart: false,
    orderState: OrderState.None
  }),
  rcbPurchasing: defaultLoadableState<RcbPurchasing>({
    hasActiveCart: false,
    orderState: RcbOrderState.None
  }),
  setFavorite() {},
  setSubscribedToNotifications() {},
  setPricingError() {},
  subscribeToNotificationsState: defaultLoadableState(false),
  transitRoute: defaultLoadableState<TransitRoute>({
    activityType: null,
    charge: 0,
    routeId: null
  }),
  triggerEvent: new EventManager(false),
  triggerFetchVdpPricing() {},
  triggerFetchVehicleAvailability() {},
  vdpPricingInfo: defaultLoadableState<Partial<VdpPricingResponse>>({}),
  vdpPricingError: PricingError.None,
  vehicleAvailability: defaultLoadableState<Availability>({
    action: AvailabilityAction.None,
    error: AvailabilityError.None,
    inventoryType: InventoryTypeKey.Core,
    isAvailableForPurchase: false,
    isBuyingAVehicle: false,
    isLockedByThisUser: false,
    isPendingDealerPurchase: false,
    isPendingDealerPurchaseByUser: false,
    lockType: VehicleLockTypeKey.None
  })
};

export const UserVehicleContext = createContext<ContextType>(defaultContext);

export const UserVehicleContextProvider = ({
  children
}: React.PropsWithChildren) => {
  const [fetchVdpPricingFlag, setFetchVdpPricingFlag] = useState(
    defaultContext.fetchVehicleAvailabilityFlag
  );
  const [fetchVehicleAvailabilityFlag, setFetchVehicleAvailabilityFlag] =
    useState(defaultContext.fetchVehicleAvailabilityFlag);
  const [isFavorite, setFavorite] = useState(defaultContext.isFavorite);
  const [isSubscribedToNotifications, setSubscribedToNotifications] = useState(
    defaultContext.isSubscribedToNotifications
  );
  const purchasing = useLoadableState<Purchasing>(
    defaultContext.purchasing.data
  );
  const rcbPurchasing = useLoadableState<RcbPurchasing>(
    defaultContext.rcbPurchasing.data
  );
  const vdpPricingInfo = useLoadableState(defaultContext.vdpPricingInfo.data);
  const [vdpPricingError, setPricingError] = useState(
    defaultContext.vdpPricingError
  );
  const subscribeToNotificationsState = useLoadableState(false);
  const transitRoute = useLoadableState<TransitRoute>(
    defaultContext.transitRoute.data
  );
  const vehicleAvailability = useLoadableState<Availability>(
    defaultContext.vehicleAvailability.data
  );

  const eventManager = useMemo(() => new EventManager(true), []);
  const availabilityUpdateFlag = useEventTrigger(eventManager, 'availability');

  const setFavoriteAndSubscribe = useCallback<ContextType['setFavorite']>(
    (f: boolean, s: boolean = false) => {
      setFavorite(f);
      setSubscribedToNotifications(s);
    },
    []
  );

  const triggerFetchVehicleAvailability = useCallback(() => {
    setFetchVehicleAvailabilityFlag(Date.now());
  }, []);

  const triggerFetchVdpPricing = useCallback(() => {
    setFetchVdpPricingFlag(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      fetchVdpPricingFlag,
      fetchVehicleAvailabilityFlag: Math.abs(
        fetchVehicleAvailabilityFlag - availabilityUpdateFlag
      ),
      isFavorite,
      isSubscribedToNotifications,
      purchasing,
      rcbPurchasing,
      setFavorite: setFavoriteAndSubscribe,
      setSubscribedToNotifications,
      setPricingError,
      subscribeToNotificationsState,
      transitRoute,
      triggerEvent: eventManager,
      triggerFetchVdpPricing,
      triggerFetchVehicleAvailability,
      vdpPricingInfo,
      vdpPricingError,
      vehicleAvailability
    }),
    [
      fetchVdpPricingFlag,
      fetchVehicleAvailabilityFlag,
      availabilityUpdateFlag,
      isFavorite,
      isSubscribedToNotifications,
      purchasing,
      rcbPurchasing,
      setFavoriteAndSubscribe,
      subscribeToNotificationsState,
      transitRoute,
      eventManager,
      triggerFetchVdpPricing,
      triggerFetchVehicleAvailability,
      vdpPricingInfo,
      vdpPricingError,
      vehicleAvailability
    ]
  );

  return (
    <UserVehicleContext.Provider value={value}>
      {children}
    </UserVehicleContext.Provider>
  );
};

UserVehicleContextProvider.displayName = 'UserVehicleContextProvider';

export default UserVehicleContext;

export const generateUserVehicleContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useVdpPricing() {
  return useContext(UserVehicleContext).vdpPricingInfo.data;
}

export function useVdpPricingTradeInfo() {
  return useContext(UserVehicleContext).vdpPricingInfo.data?.tradeInfo ?? {};
}

export function useVdpPaymentTerms(): Partial<VehiclePaymentTerm> {
  const vdpPricingInfo = useVdpPricing();

  return vdpPricingInfo.price ?? {};
}

export function useTransitRoute() {
  return useContext(UserVehicleContext).transitRoute.data;
}

export function useVehicleAvailability() {
  return useContext(UserVehicleContext).vehicleAvailability.data;
}
